import type { Task, Priority } from "../tasks/types.js";
import type { RecurrenceRule } from "../recurrence/types.js";
import type { RecurrenceOverride } from "../recurrence/overrides/types.js";
import { escapeText, foldLine, formatUtc, formatDateProp, rruleWithUntil } from "./escape.js";
import { buildVTimezone } from "./vtimezone.js";
import type { IcsExportInput } from "./types.js";

/**
 * Render tasks/events/recurrence to a single VCALENDAR string (RFC 5545).
 *
 * - dated entries → VEVENT; undated → VTODO.
 * - recurrence → RRULE + UNTIL; cancelled occurrences → EXDATE; modified single
 *   occurrences → companion VEVENTs sharing the UID with a RECURRENCE-ID.
 * - reminders → VALARM TRIGGER; tz → DTSTART;TZID + a synthesized VTIMEZONE.
 * - app-specific fidelity (event-vs-task, color, calendar, topics) rides on
 *   X-REMINDERS-* properties so a round-trip into this app is lossless.
 */
export function exportIcs(input: IcsExportInput): string {
  const prodId = input.productId ?? "reminders.app";
  const now = input.now ?? new Date();
  const dtstamp = formatUtc(now.toISOString());

  const usedZones = new Set<string>();
  const blocks: string[] = [];

  for (const task of input.tasks) {
    if (task.virtual || task.deleted_at) continue;
    const rule = task.recurrence_id ? input.rulesById[task.recurrence_id] : undefined;
    const tz = task.tz ?? rule?.tz ?? null;
    if (tz && !task.all_day) usedZones.add(tz);
    const ovs = rule ? input.overrides.filter((o) => o.recurrence_id === rule.id) : [];
    blocks.push(...buildComponents(task, rule, ovs, input, dtstamp, tz));
  }

  const vtimezones = [...usedZones].map((tz) => buildVTimezone(tz, new Date(now).getUTCFullYear()));

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:-//${prodId}//Reminders//EN`,
    "CALSCALE:GREGORIAN",
    ...vtimezones,
    ...blocks,
    "END:VCALENDAR",
  ];
  return lines.map(foldLine).join("\r\n") + "\r\n";
}

function buildComponents(
  task: Task,
  rule: RecurrenceRule | undefined,
  ovs: RecurrenceOverride[],
  input: IcsExportInput,
  dtstamp: string,
  tz: string | null,
): string[] {
  const uid = task.ical_uid ?? `${task.id}@reminders.app`;
  if (task.due_at == null) return buildVTodo(task, input, dtstamp, uid);

  const out = buildVEvent(task, rule, ovs, input, dtstamp, tz, uid);
  // Modified single occurrences → companion VEVENTs with RECURRENCE-ID.
  for (const o of ovs) {
    if (o.range !== "single" || o.status === "cancelled") continue;
    out.push(...buildOverrideVEvent(task, o, input, dtstamp, tz, uid));
  }
  return out;
}

function buildVEvent(
  task: Task,
  rule: RecurrenceRule | undefined,
  ovs: RecurrenceOverride[],
  input: IcsExportInput,
  dtstamp: string,
  tz: string | null,
  uid: string,
): string[] {
  const l: string[] = ["BEGIN:VEVENT", `UID:${uid}`, `DTSTAMP:${dtstamp}`];
  pushDate(l, "DTSTART", task.due_at!, tz, task.all_day);
  pushEnd(l, task, tz);
  l.push(`SUMMARY:${escapeText(task.title)}`);
  pushTextProps(l, task, input);
  if (rule) pushRecurrence(l, rule, ovs, tz, task.all_day);
  pushPriority(l, task.priority);
  l.push(`STATUS:${eventStatus(task.status)}`);
  pushXProps(l, task, input, true);
  pushAlarms(l, task);
  l.push("END:VEVENT");
  return l;
}

function buildOverrideVEvent(
  task: Task,
  o: RecurrenceOverride,
  input: IcsExportInput,
  dtstamp: string,
  tz: string | null,
  uid: string,
): string[] {
  const start = o.due_at ?? o.recurrence_id_ref;
  const allDay = o.all_day ?? task.all_day;
  const l: string[] = ["BEGIN:VEVENT", `UID:${uid}`, `DTSTAMP:${dtstamp}`];
  pushDate(l, "RECURRENCE-ID", o.recurrence_id_ref, tz, allDay);
  pushDate(l, "DTSTART", start, tz, allDay);
  const dur = o.duration_minutes ?? task.duration_minutes;
  if (!allDay && dur > 0) pushDate(l, "DTEND", addMinutesIso(start, dur), tz, false);
  l.push(`SUMMARY:${escapeText(o.title ?? task.title)}`);
  const notes = o.notes ?? task.notes;
  if (notes) l.push(`DESCRIPTION:${escapeText(notes)}`);
  pushPriority(l, o.priority ?? task.priority);
  if (o.status) l.push(`STATUS:${eventStatus(o.status === "cancelled" ? "cancelled" : o.status)}`);
  pushXProps(l, task, input, true);
  l.push("END:VEVENT");
  return l;
}

function buildVTodo(task: Task, input: IcsExportInput, dtstamp: string, uid: string): string[] {
  const l: string[] = ["BEGIN:VTODO", `UID:${uid}`, `DTSTAMP:${dtstamp}`];
  if (task.deadline_at) pushDate(l, "DUE", task.deadline_at, task.tz, false);
  l.push(`SUMMARY:${escapeText(task.title)}`);
  pushTextProps(l, task, input);
  pushPriority(l, task.priority);
  l.push(`STATUS:${task.status === "done" ? "COMPLETED" : task.status === "cancelled" ? "CANCELLED" : "NEEDS-ACTION"}`);
  pushXProps(l, task, input, false);
  pushAlarms(l, task);
  l.push("END:VTODO");
  return l;
}

function pushDate(l: string[], prop: string, iso: string, tz: string | null, allDay: boolean): void {
  const { params, value } = formatDateProp(iso, tz, allDay);
  l.push(`${prop}${params}:${value}`);
}

function pushEnd(l: string[], task: Task, tz: string | null): void {
  if (task.all_day) {
    const days = task.duration_minutes > 0 ? Math.ceil(task.duration_minutes / 1440) : 1;
    pushDate(l, "DTEND", addMinutesIso(task.due_at!, days * 1440), tz, true);
  } else if (task.duration_minutes > 0) {
    pushDate(l, "DTEND", addMinutesIso(task.due_at!, task.duration_minutes), tz, false);
  }
}

function pushTextProps(l: string[], task: Task, _input: IcsExportInput): void {
  if (task.notes) l.push(`DESCRIPTION:${escapeText(task.notes)}`);
  if (task.location) l.push(`LOCATION:${escapeText(task.location)}`);
}

function pushRecurrence(
  l: string[],
  rule: RecurrenceRule,
  ovs: RecurrenceOverride[],
  tz: string | null,
  allDay: boolean,
): void {
  l.push(`RRULE:${rruleWithUntil(rule.rrule, rule.until)}`);
  const cancels = ovs.filter((o) => o.range === "single" && o.status === "cancelled");
  if (cancels.length) {
    // EXDATE's VALUE type must match DTSTART — DATE for an all-day series, else DATE-TIME.
    const { params } = formatDateProp(cancels[0]!.recurrence_id_ref, tz, allDay);
    const values = cancels.map((o) => formatDateProp(o.recurrence_id_ref, tz, allDay).value);
    l.push(`EXDATE${params}:${values.join(",")}`);
  }
}

function pushPriority(l: string[], p: Priority): void {
  const n = p === "high" ? 1 : p === "medium" ? 5 : p === "low" ? 9 : 0;
  if (n) l.push(`PRIORITY:${n}`);
}

function pushXProps(l: string[], task: Task, input: IcsExportInput, isEvent: boolean): void {
  l.push(`X-REMINDERS-IS-EVENT:${isEvent && task.is_event ? "TRUE" : "FALSE"}`);
  if (task.custom_color) l.push(`X-REMINDERS-COLOR:${task.custom_color}`);
  if (task.priority !== "none") l.push(`X-REMINDERS-PRIORITY:${task.priority}`);
  const cats = task.label_ids.map((id) => input.labelsById[id]?.name).filter(Boolean) as string[];
  if (cats.length) l.push(`CATEGORIES:${cats.map(escapeText).join(",")}`);
  if (task.calendar_id) {
    const name = input.calendarsById[task.calendar_id]?.name;
    if (name) l.push(`X-REMINDERS-CALENDAR:${escapeText(name)}`);
  }
  const topics = task.topic_ids.map((id) => input.topicsById?.[id]?.name).filter(Boolean) as string[];
  if (topics.length) l.push(`X-REMINDERS-TOPICS:${topics.map(escapeText).join(",")}`);
}

function pushAlarms(l: string[], task: Task): void {
  for (const m of task.reminder_offsets) {
    l.push("BEGIN:VALARM", "ACTION:DISPLAY", `DESCRIPTION:${escapeText(task.title)}`, `TRIGGER:${trigger(m)}`, "END:VALARM");
  }
}

function trigger(minutesBefore: number): string {
  return minutesBefore <= 0 ? "PT0S" : `-PT${minutesBefore}M`;
}

function eventStatus(s: Task["status"]): string {
  // ICS VEVENT has no "done" status; only cancellation maps cleanly.
  return s === "cancelled" ? "CANCELLED" : "CONFIRMED";
}

function addMinutesIso(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();
}
